import Hls from 'hls.js';
import { VideoSources, VideoConfig } from '../types/video';

/**
 * Video storage configuration
 * Set REACT_APP_USE_LOCAL_VIDEOS=true in .env to use local videos from public folder
 * Set to false (or remove) to use CloudFront CDN
 */
const USE_LOCAL_VIDEOS = process.env.REACT_APP_USE_LOCAL_VIDEOS === 'true';

const CDN_BASE_URL = 'https://cdn.xplorius.com/assets/videos-optimized';
const LOCAL_BASE_URL = '/videos-processed';

// Use local or CDN based on environment variable
const BASE_URL = USE_LOCAL_VIDEOS ? LOCAL_BASE_URL : CDN_BASE_URL;

/**
 * Video configuration for all categories
 */
export const VIDEO_CONFIG: VideoConfig = {
  baseUrl: BASE_URL,
  categories: {
    // edit/maldives point to fresh "-2.0" CDN folders so CloudFront serves them
    // as cache misses (no invalidation needed). New counts: edit 8→9, maldives 5→8.
    edit: { path: 'edit-2.0', count: 9 },
    maldives: { path: 'maldives-2.0', count: 8 },
    safari: { path: 'safari', count: 5 },
    trusted: { path: 'trusted', count: 2 },
    hero: { path: 'hero', count: 1 },
  },
};

/**
 * Get video sources for a specific category and ID
 *
 * @param category - Video category (edit, maldives, safari, trusted, hero-grid, hero-mobile)
 * @param id - Video ID (1, 2, 3, etc.)
 * @returns VideoSources object with all format URLs
 *
 * @example
 * ```ts
 * const sources = getVideoSources('edit', 1);
 * // {
 * //   mp4: '/videos-processed/edit/1/video.mp4',  // local
 * //   mp4: 'https://cdn.xplorius.com/assets/videos-optimized/edit/1/video.mp4',  // CDN
 * //   ...
 * // }
 * ```
 */
// Categories whose posters were re-shot and uploaded as poster-v2.jpg on their
// *existing* CDN path (to dodge cache). edit/maldives no longer need this — they
// live under fresh "-2.0" folders where plain poster.jpg is already cache-free.
const POSTER_V2_CATEGORIES = new Set(['safari']);

// Per-video poster overrides, keyed by `category:id`. Used when a single video's
// poster was re-uploaded under a fresh filename to dodge CloudFront cache (no
// invalidation available) — e.g. edit/1 corrected after a wrong frame went live.
const POSTER_OVERRIDES: Record<string, string> = {
  'edit:1': 'poster-v2.jpg',
};

export function getVideoSources(category: string, id: number | string): VideoSources {
  // Resolve the CDN folder via the category's `path` (e.g. edit → edit-2.0),
  // falling back to the category name for any caller not in VIDEO_CONFIG.
  const pathSegment = VIDEO_CONFIG.categories[category]?.path ?? category;
  const basePath = `${BASE_URL}/${pathSegment}/${id}`;
  const posterFile =
    POSTER_OVERRIDES[`${category}:${id}`] ??
    (POSTER_V2_CATEGORIES.has(category) ? 'poster-v2.jpg' : 'poster.jpg');

  return {
    mp4: `${basePath}/video.mp4`,
    webm: `${basePath}/video.webm`,
    hls: `${basePath}/hls/master.m3u8`,
    poster: `${basePath}/${posterFile}`,
  };
}

export function getHeroVideoSources(): VideoSources {
  return getVideoSources('hero', 'v2');
}

/**
 * Generate array of video data for a category
 *
 * @param category - Video category
 * @returns Array of VideoSources for all videos in the category
 *
 * @example
 * ```ts
 * const editVideos = generateVideoDataArray('edit');
 * // Returns 8 VideoSources objects for edit videos
 * ```
 */
export function generateVideoDataArray(category: string): Array<{ id: number; sources: VideoSources }> {
  const config = VIDEO_CONFIG.categories[category];
  if (!config) return [];

  return Array.from({ length: config.count }, (_, i) => ({
    id: i + 1,
    sources: getVideoSources(category, i + 1),
  }));
}

function supportsWebM(): boolean {
  if (typeof document === 'undefined') return false;

  const video = document.createElement('video');
  return !!video.canPlayType('video/webm; codecs="vp9"');
}

/**
 * Get the best video source based on browser capabilities
 *
 * Priority:
 * 1. HLS (if available and browser supports it)
 * 2. WebM (if browser supports VP9)
 * 3. MP4 (universal fallback)
 *
 * @param sources - VideoSources object
 * @returns Best source URL for the current browser
 */
export function getBestSource(sources: VideoSources): string {
  // HLS is handled separately via hls.js, so skip it here
  // This function is for direct source selection

  if (sources.webm && supportsWebM()) {
    return sources.webm;
  }

  return sources.mp4;
}

/**
 * Initialize HLS on a video element with automatic fallback
 *
 * Priority:
 * 1. Native HLS (Safari/iOS)
 * 2. hls.js (Chrome, Firefox, Edge)
 * 3. WebM fallback (if HLS fails)
 * 4. MP4 fallback (universal)
 *
 * @param video - The HTML video element
 * @param sources - VideoSources object with all format URLs
 * @returns Hls instance (or null) - MUST be destroyed when done
 *
 * @example
 * ```tsx
 * const hlsInstance = useRef<Hls | null>(null);
 *
 * const loadVideo = () => {
 *   hlsInstance.current = initHLSOnVideo(videoRef.current, sources);
 * };
 *
 * useEffect(() => {
 *   return () => hlsInstance.current?.destroy();
 * }, []);
 * ```
 */
export function initHLSOnVideo(
  video: HTMLVideoElement | null,
  sources: VideoSources
): Hls | null {
  if (!video || !sources.hls) {
    // No HLS source, use best available
    if (video) {
      video.src = getBestSource(sources);
    }
    return null;
  }

  // Native HLS support (Safari, iOS)
  if (video.canPlayType('application/vnd.apple.mpegurl')) {
    video.src = sources.hls;
    return null;
  }

  // Use hls.js for other browsers
  if (Hls.isSupported()) {
    const hls = new Hls({
      startLevel: 1, // Start at 720p, then adapt based on bandwidth
      capLevelToPlayerSize: true,
      maxBufferLength: 30,
      maxMaxBufferLength: 60,
      enableWorker: true,
    });

    hls.loadSource(sources.hls);
    hls.attachMedia(video);

    // Handle fatal errors with fallback
    hls.on(Hls.Events.ERROR, (_, data) => {
      if (data.fatal) {
        console.warn('[HLS] Fatal error, falling back:', data.details);
        hls.destroy();

        // Fallback to WebM or MP4
        video.src = getBestSource(sources);
      }
    });

    return hls;
  }

  // No HLS support at all - use fallback
  video.src = getBestSource(sources);
  return null;
}
